/* eslint-disable complexity */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import { Menu, MenuDivider, MenuItem, MenuSegmentTitle } from 'carbon-react/lib/components/menu';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UIProductIcon } from '@futrli/ui-components';
import {
  matchSettingsPath,
  storeCurrentLocation,
  ROUTES,
  checkNavPermission,
  workspaceByIdSelector,
  getEditConsolidationUrl,
  consolidationsSelector,
  getSfaOnboardingUrl
} from '@futrli/shared-services';
import { OrganisationListItem, Permission, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { Organisation } from '@futrli-sdk/organisations';
import { Consolidation } from '@futrli-sdk/consolidations';
import { useSxpContext } from '@futrli/predict';
import { getWorkspaceListingUrl, getWorkspaceUrl } from '@futrli/predict-next/workspaces/utils/route-utils';

import * as styles from './organisations-menu.scss';
import { StoreState } from '../../core/redux-utils/types';
import { EXIT_SETTINGS_KEY } from '../../core/constants';

interface Props {
  readonly permissions: Permission[];
  workspaceIdProp?: string;
  organisationIdProp?: string;
}

export const OrganisationsMenu = ({ permissions, workspaceIdProp, organisationIdProp }: Props) => {
  const [menuKey, setMenuKey] = useState(0);
  const { workspaceId: workspaceIdParam, organisationId: organisationIdParam } = useParams();
  const workspaceId = workspaceIdParam || workspaceIdProp || '';
  const organisationId = organisationIdParam || organisationIdProp || '';
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const hasUsersPermissions = checkNavPermission(permissions, ROUTES.MEMBERS_ROUTE);
  const { onlyExternalOrgs } = useSxpContext();

  let organisations: (Organisation | OrganisationListItem | Consolidation)[] | null = useSelector(
    (store: StoreState) => {
      const { normalised } = store;
      const currentWorkspace = workspaceByIdSelector(normalised, workspaceId);
      const storedOrgs = currentWorkspace ? (currentWorkspace as WorkspacesListingItem).organisations : null;
      return storedOrgs;
    }
  );

  const consolidations = useSelector((store: StoreState) => {
    const { normalised } = store;
    return consolidationsSelector(normalised) || {};
  });

  if (Array.isArray(organisations)) {
    organisations = [...organisations, ...Object.values(consolidations)];
  }

  const organisation = (organisations as Organisation[])?.filter(o => o.id === organisationId)[0];

  const handleConsolidationSettingsClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    e.stopPropagation();
    navigate(getEditConsolidationUrl(workspaceId, id));
  };

  const _storeCurrentLocation = () => {
    // Let's not store the exit link if we're already in settings
    if (!matchSettingsPath(location.pathname)) {
      storeCurrentLocation(EXIT_SETTINGS_KEY);
    }
  };

  const handleOrganisationsClick = () => {
    _storeCurrentLocation();
    navigate(getWorkspaceListingUrl(workspaceId));
  };

  const handleSwitchOrganisationClick = (o: Organisation | OrganisationListItem | Consolidation) => {
    setMenuKey(prev => prev + 1); // force remount to close dropdown
    const bid = (o as OrganisationListItem).sxp_business_id || '';
    if ((o as Consolidation).display_currency) {
      navigate(`/workspaces/${workspaceId}/consolidations/${o.id}`);
    } else if ((o as OrganisationListItem).is_holding) {
      navigate(getSfaOnboardingUrl(bid));
    } else {
      navigate(`/workspaces/${workspaceId}/organisations/${o.id}${bid ? `?bid=${bid}` : ''}`);
    }
  };

  const handleUsersClick = () => {
    _storeCurrentLocation();
    navigate(getWorkspaceUrl(workspaceId, 'users'));
  };

  const getOrganisationName = (organisation?: Organisation | OrganisationListItem | Consolidation) => {
    if (organisation) {
      const isConsolidation = (organisation as Consolidation).display_currency;
      const consolidationSuffix = intl.formatMessage({
        id: 'common.consolidated_suffix',
        defaultMessage: ' (consolidated)'
      });
      return `${organisation?.name}${isConsolidation ? consolidationSuffix : ''}`;
    }
  };

  if (!organisationId || !organisations) {
    return null;
  }

  return (
    <Box>
      <Menu menuType="black" key={menuKey}>
        <MenuItem
          submenu={getOrganisationName(organisation)}
          minWidth={100}
          maxWidth={700}
          submenuDirection="right"
          submenuMinWidth="150px"
          submenuMaxWidth="800px"
        >
          <MenuItem onClick={handleOrganisationsClick}>
            <FormattedMessage id="common.manage_organisations" defaultMessage="Manage Organisations" />
          </MenuItem>
          {hasUsersPermissions && !onlyExternalOrgs ? (
            <MenuItem onClick={handleUsersClick}>
              <FormattedMessage id="common.manage_users" defaultMessage="Manage Users" />
            </MenuItem>
          ) : null}

          {organisations && organisations.length > 1 && (
            <div style={{ maxHeight: 418 }}>
              <MenuDivider />
              <MenuSegmentTitle
                text={intl.formatMessage({ id: 'common.switch_business', defaultMessage: 'Switch business' })}
              >
                {(organisations as [])
                  .sort(
                    (
                      a: Organisation | OrganisationListItem | Consolidation,
                      b: Organisation | OrganisationListItem | Consolidation
                    ) => a.name.localeCompare(b.name)
                  )
                  .map((o: Organisation | OrganisationListItem | Consolidation) => (
                    <div key={o.id} className={styles.menuItemParent}>
                      <MenuItem onClick={() => handleSwitchOrganisationClick(o)} key={o.id}>
                        <div className={styles.menuItemContent}>
                          <div>{getOrganisationName(o)}</div>
                          {((o as unknown) as Consolidation).display_currency ? (
                            <div className={styles.cogButton} onClick={e => handleConsolidationSettingsClick(e, o.id)}>
                              <UIProductIcon iconName="predict-settings" className={styles.uiMenuSettingsIcon} />
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>
                      </MenuItem>
                    </div>
                  ))}
              </MenuSegmentTitle>
            </div>
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};
